import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { CreateProjectDialog } from "@/domains/projects/components/CreateProjectDialog";
import { CreateProjectDialogContext } from "@/domains/projects/components/createProjectDialogContext";
import { usePermissions } from "@/hooks/usePermissions";

/**
 * Owns the create-project dialog the building page's project PICKERS share.
 *
 * Not the only one on the page: `ProjectsEmptyState` and
 * `DashboardProjectsGrid` each keep their own, because creating from those
 * surfaces is not a scope change and must not drag the user into the new
 * project. This one serves the sidebar view-mode toggle's dropdown, whose
 * popover closes before the dialog opens — so the dialog cannot live inside
 * it.
 *
 * The dialog is mounted unconditionally — never `{open && …}`, and never
 * behind `canCreateProjects` either: it closes itself the moment the mutation
 * fires, and React Query drops a mutate-level `onSuccess` whose component
 * unmounted first, so ANY condition that can go false mid-flight silently
 * loses the follow-through step. A permission refetch is one of those, and
 * `usePermissions` answers all-false while the org context re-resolves. The
 * permission gates the open path instead (`openCreateProject` below, and the
 * triggers that read `canCreateProjects` off this context), which is the only
 * way in.
 *
 * Being mounted always is not licence to FETCH always: the dialog takes
 * `useProjects(buildingId, { list: false })`, so a user who can never open it
 * pays nothing for it on a building navigation. Cheapness is what makes the
 * unconditional mount affordable — keep the query off rather than reaching for
 * a mount gate that a permission blip can pull out from under a live create.
 *
 * `onProjectCreated` is that follow-through, and it belongs to the LAYOUT
 * rather than to the picker that opened the dialog: the dialog outlives the
 * picker's popover, and a `setProject` from an unmounted `useBuildingScope`
 * reads the location as it was when that instance last rendered, landing the
 * create on the page the user has left. A callback from
 * a component mounted for the whole building page cannot go stale that way.
 */
export function CreateProjectDialogProvider({
  buildingId,
  onProjectCreated,
  children,
}: {
  buildingId: string;
  /** Fired with the new project's id once the create mutation succeeds. */
  onProjectCreated: (projectId: number) => void;
  children: ReactNode;
}) {
  const { canCreateProjects } = usePermissions();
  // BuildingLayout is not remounted when the route changes building, so a
  // dialog left open across that change survives it — and both halves of it
  // are then bound to the building the user has left: the form would file the
  // project against the new building while the follow-through it is carrying
  // scopes the old one (`setProject` remembers under the building id it closed
  // over, and the pick it navigates resolves against that building's path).
  // Neither is what anyone asked for, so the dialog is open only for the
  // building it was opened on — derived, so the change closes it in the same
  // render rather than an effect closing it in a second one.
  const [openForBuilding, setOpenForBuilding] = useState<string | null>(null);
  const open = openForBuilding === buildingId;
  // The building the pending create belongs to. A ref rather than the state
  // above, which is already null by the time the create resolves — the dialog
  // closes itself the moment the mutation fires.
  const pendingBuildingRef = useRef<string | undefined>(undefined);

  // Deriving `open` closes the dialog on a building change, but it does not
  // clear the state behind it: the close is controlled and the dialog did not
  // make it, so no `onOpenChange` fires and `openForBuilding` stays on the
  // building the user left. Coming back to it would then reopen the dialog —
  // and re-arm a follow-through — nobody asked for.
  //
  // Adjusted during render rather than in an effect: React re-runs this
  // component before committing, so nothing downstream ever sees the state of
  // the building the user left, and no second commit is spent undoing it
  // (react.dev, "You Might Not Need an Effect" → adjusting state when a prop
  // changes). A `key` on this component would do the same, but it would take
  // the whole building page down with it — `children` render inside.
  const [seenBuildingId, setSeenBuildingId] = useState(buildingId);
  if (seenBuildingId !== buildingId) {
    setSeenBuildingId(buildingId);
    setOpenForBuilding(null);
  }

  // The pending create goes the same way, for the same reason `handleCreated`
  // drops one that crossed buildings: it belongs to the building it was
  // started in, whose dialog is gone. In an effect, NOT in the block above —
  // a ref may not be written while rendering (react.dev, `useRef`), and a
  // render React throws away would move it with nothing to put it back.
  useEffect(() => {
    pendingBuildingRef.current = undefined;
  }, [buildingId]);

  const openCreateProject = useCallback(() => {
    if (!canCreateProjects) return;
    pendingBuildingRef.current = buildingId;
    setOpenForBuilding(buildingId);
  }, [canCreateProjects, buildingId]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // Closing without having created is the user backing out — Cancel, Esc
      // and a click outside all close while the create is in flight, and the
      // mutation is not cancelled by any of them. Forgetting the pending
      // building here is what makes the late success land on nothing: the
      // project is still created, but the follow-through that would re-scope
      // and navigate into it is dropped, because the user asked to leave.
      // The dialog reports a successful create *before* it closes, so this
      // never eats a real follow-through.
      if (!next) pendingBuildingRef.current = undefined;
      setOpenForBuilding(next ? buildingId : null);
    },
    [buildingId]
  );

  const handleCreated = useCallback(
    (projectId: number) => {
      const pendingBuilding = pendingBuildingRef.current;
      // Cleared whether or not it fires, so the next create can't re-run this
      // one's follow-through.
      pendingBuildingRef.current = undefined;
      // A create still in flight when the route changed building belongs to
      // the building it was started in, so its follow-through is dropped for
      // the same reason the dialog closed.
      if (pendingBuilding !== buildingId) return;
      onProjectCreated(projectId);
    },
    [buildingId, onProjectCreated]
  );

  const value = useMemo(
    () => ({ openCreateProject, canCreateProjects }),
    [openCreateProject, canCreateProjects]
  );

  return (
    <CreateProjectDialogContext.Provider value={value}>
      {children}
      <CreateProjectDialog
        open={open}
        onOpenChange={handleOpenChange}
        buildingId={buildingId}
        onCreated={handleCreated}
      />
    </CreateProjectDialogContext.Provider>
  );
}
